<template>
  <div
    v-if="pageLoading"
    class="m-3"
  >
    <placeholder-content :is-load="true" />
  </div>
  <div class="m-3" v-else-if="!hasPermission">
    <access-denied />
  </div>
  <div
    v-else
  >
    <div class="d-flex border-bottom mb-3 justify-content-between align-items-end">
      <div class="overflow-auto hide-scrollbar flex-grow-1">
        <ul class="nav nav-custom flex-nowrap">
          <li
            v-for="tb in tabs"
            :key="tb.value"
            class="nav-item cursor-pointer"
            @click="setTab(tb.value)"
          >
            <a class="nav-link text-nowrap" :class="{ 'active': filter.tab === tb.value }">
              {{ $t(tb.label) }}
              <span class="badge rounded-pill ms-1" :class="'bg-' + (tb.color || 'primary')">
                {{ counts[tb.value] || 0 }}
              </span>
            </a>
          </li>
        </ul>
      </div>
      <column-toggle
        :headers="allHeaders"
        storage-key="taxInvoices.columns"
        v-model="visibleColumns"
      />
    </div>
    <div class="d-flex justify-content-start mx-3 flex-column flex-sm-row flex-wrap">
      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto">
        <input
          type="search"
          class="form-control"
          :placeholder="$t('search')+'...'"
          aria-label="Search"
          v-model.trim="params.search"
          @keyup.enter="fetchData"
        >
        <button
          class="input-group-text"
          type="button"
          @click="fetchData"
        >
          <i class="fas fa-search" />
        </button>
      </div>
      <div class="custom-input-group input-group input-group-sm me-sm-2 mb-3 w-auto date-range-input-group">
        <span class="input-group-text">
          <i class="fa-regular fa-calendar"/>
        </span>
        <date-range-picker
          ref="dateRangePicker"
          v-model="params.date_range"
          :date-range="params.date_range"
          :locale-data="dateRangeLocale"
          :ranges="false"
          :opens="'right'"
          :auto-apply="true"
          :show-dropdowns="true"
          :control-container-class="''"
        >
          <template v-slot:input="picker">
            <span v-if="picker.startDate && picker.endDate">
              {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
            </span>
            <span v-else class="text-muted">
              {{ $t('Select date range') }}
            </span>
          </template>
        </date-range-picker>
        <button
          v-if="params.date_range && params.date_range.startDate && params.date_range.endDate"
          type="button"
          class="input-group-text"
          @click="clearDateRange"
        >
          <i class="fa-solid fa-xmark"/>
        </button>
      </div>
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary text-nowrap position-relative me-sm-2 mb-3"
        @click="offcanvasFilter.open = true"
      >
        <i class="fa-solid fa-filter me-1" />
        {{ $t('Filters') }}
        <span v-if="activeFilterCount" class="badge bg-primary ms-1 rounded-pill">
          {{ activeFilterCount }}
        </span>
      </button>
      <per-page
        @per-page="setPerPage"
      />
    </div>
    <vue3-easy-data-table
      v-model:server-options="datatable.serverOptions"
      ref="dataTable"
      :headers="visibleHeaders"
      :items="datatable.items"
      :loading="datatable.loading"
      :server-items-length="datatable.total"
      :theme-color="'#515151'"
      :hide-footer="false"
      :hide-rows-per-page="true"
      :empty-message="$t('No Available Data')"
      :table-min-height="null"
      :body-item-class-name="'align-top py-1 px-2'"
      buttons-pagination
      table-class-name='customize-table'
    >
      <template #item-invoice_no="item">
        <div class="fw-bold">{{ item.invoice_no }}</div>
        <div class="text-muted small">{{ item.created_by }}</div>
      </template>

      <template #item-bill_no="item">
        {{ item.bill_no || '-' }}
      </template>

      <template #item-branch="item">
        {{ item.branch || '-' }}
      </template>

      <template #item-customer="item">
        <div>{{ item.customer_name || '-' }}</div>
        <div v-if="item.customer_tax_id" class="text-muted small">
          {{ item.customer_tax_id }}
          <span v-if="item.customer_branch">({{ item.customer_branch }})</span>
        </div>
      </template>

      <template #item-total_amount="item">
        <div class="text-end fw-semibold">
          {{ $storeIndex.formatThaiCurrency(toNumber(item.total_amount)) }}
        </div>
      </template>

      <template #item-created_at="item">
        {{ formatDateTime(item.created_at) }}
      </template>

      <template #item-status="item">
        <template v-if="item.voided_at">
          <span class="badge bg-danger">{{ $t('Voided') }}</span>
          <div v-if="item.void_reason" class="text-muted small mt-1">
            {{ item.void_reason }}
          </div>
          <div v-if="item.voided_by" class="text-muted small">
            {{ $t('Voided By') }}: {{ item.voided_by }}
          </div>
        </template>
        <span v-else class="badge bg-success">{{ $t('Active') }}</span>
      </template>

      <template #item-action="item">
        <div class="icon-link custom-actions">
          <!--
            พิมพ์ซ้ำใบกำกับเต็มรูป (A4) — หน้านี้เดิมมีแต่ปุ่มยกเลิก ทั้งที่เป็นหน้า "จัดการใบกำกับ"
            โชว์กับใบที่ถูกยกเลิกด้วย เพราะบัญชีต้องพิมพ์ไปแนบแฟ้มได้เหมือนกัน
          -->
          <popper :placement="'top'" :hover="true" :arrow="true" :disableClickAway="true" class="popper-default border-0 m-0">
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm btn-icon"
              :disabled="printingId === item.id"
              @click="printInvoice(item)"
            >
              <i :class="printingId === item.id ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-print'" />
            </button>
            <template #content>
              <div>{{ $t('Print Tax Invoice') }}</div>
            </template>
          </popper>

          <popper
            v-if="!item.voided_at"
            :placement="'top'"
            :hover="true"
            :arrow="true"
            :disableClickAway="true"
            class="popper-default border-0 m-0"
          >
            <button
              type="button"
              class="btn btn-outline-danger btn-sm btn-icon"
              @click="quickVoid(item)"
            >
              <i class="fa-solid fa-ban" />
            </button>
            <template #content>
              <div>{{ $t('Void Tax Invoice') }}</div>
            </template>
          </popper>
          <span v-else class="text-muted">-</span>
        </div>
      </template>
      <template #loading>
        <i class="fa-solid fa-spinner fa-spin fa-2xl" />
      </template>
    </vue3-easy-data-table>

    <offcanvas-filter
      v-if="offcanvasFilter.open"
      :sections="filterSections"
      :value="{ branch_ids: params.branch_ids }"
      @apply="onApplyFilter"
      @close="offcanvasFilter.open = false"
    />
  </div>
</template>

<script lang="js">
import Vue3EasyDataTable from 'vue3-easy-data-table';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from '@/plugins/AccessDenied.vue';
import PerPage from '@/plugins/PerPage.vue';
import ColumnToggle from '@/components/shared/ColumnToggle.vue';
import OffcanvasFilter from '@/components/shared/OffcanvasFilter.vue';
import { loadVisibleColumns } from '@/utils/columnToggle';
import moment from 'moment';
import Popper from 'vue3-popper';
import DateRangePicker from 'vue3-daterange-picker';
import FieldsDef from './assets/template/datatable/FieldsDef';
import { TaxInvoices } from '@/services/endpoint/tax-invoices.service';
import { Bills } from '@/services/endpoint/bills.service';
import { buildTaxInvoiceA4DocDefinition, printPdf } from '@/utils/taxInvoiceReceipt';
import { preparePrintConfig } from '@/utils/billReceipt';

const TAB_ORDER = ['all', 'active', 'voided'];
const TAB_META = {
  all:    { label: 'All',    color: 'primary' },
  active: { label: 'Active', color: 'success' },
  voided: { label: 'Voided', color: 'danger' }
};

export default {
  components: {
    Vue3EasyDataTable,
    PlaceholderContent,
    AccessDenied,
    PerPage,
    ColumnToggle,
    OffcanvasFilter,
    Popper,
    DateRangePicker
  },
  data() {
    return {
      pageLoading: false,
      hasPermission: true,
      // id ของใบที่กำลังพิมพ์ — โชว์ spinner เฉพาะแถวนั้น
      printingId: null,
      params: {
        limit: 10,
        page: 1,
        search: '',
        branch_ids: [],
        date_range: { startDate: null, endDate: null }
      },
      filter: {
        tab: 'active'
      },
      filters: {
        branches: []
      },
      counts: { all: 0, active: 0, voided: 0 },
      datatable: {
        loading: false,
        headers: FieldsDef,
        items: [],
        total: 0,
        serverOptions: {
          page: 1,
          rowsPerPage: 10,
          sortBy: 'created_at',
          sortType: 'desc'
        }
      },
      offcanvasFilter: { open: false },
      visibleColumns: loadVisibleColumns('taxInvoices.columns', FieldsDef(t => t))
    };
  },
  computed: {
    allHeaders() {
      return this.datatable.headers(this.$t);
    },
    visibleHeaders() {
      return this.allHeaders.filter(h => !h.togglable || this.visibleColumns.includes(h.value));
    },
    dateRangeLocale() {
      return {
        firstDay: 1,
        format: 'yyyy-mm-dd',
        applyLabel:  this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        clearable:   true,
        monthNames:  this.$storeIndex.monthNames(),
        daysOfWeek:  this.$storeIndex.daysOfWeek()
      };
    },
    tabs() {
      return TAB_ORDER.map(v => ({ value: v, ...TAB_META[v] }));
    },
    activeFilterCount() {
      return this.params.branch_ids.length;
    },
    filterSections() {
      return [
        {
          key: 'branch_ids',
          label: 'Branch',
          icon: 'fa-building',
          options: (this.filters.branches || []).map(b => ({ id: b.id, name: b.name }))
        }
      ];
    }
  },
  async mounted() {
    this.pageLoading = true;
    await this.fetchData();
    this.pageLoading = false;
  },
  methods: {
    /**
     * พิมพ์ซ้ำใบกำกับเต็มรูปของแถวนั้น — ประกอบเอกสารจาก 3 แหล่งเหมือนหน้าจัดการบิล:
     * รายละเอียดบิล + ค่าภาษีของร้าน (filter.shop_tax) + ที่อยู่/โลโก้สาขา (branch_config)
     */
    async printInvoice(item) {
      if (!item?.bill_id || this.printingId === item.id) return;
      this.printingId = item.id;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      const popup = isMobile ? window.open('', '_blank') : null;
      try {
        const info = await Bills.info(item.bill_id);
        const bill = info.data.data;
        const shopTax = info.data?.filter?.shop_tax || {};

        let config = {};
        try {
          const res = await Bills.print(item.bill_id);
          config = await preparePrintConfig(res.data?.filter?.branch_config || {});
        } catch (e) {
          config = {};
        }

        await printPdf(buildTaxInvoiceA4DocDefinition({
          bill,
          config,
          shopTax,
          fmt: (v) => this.$storeIndex.formatThaiCurrency(v),
          t: (s) => this.$t(s),
        }), { popup });
      } catch (err) {
        if (popup) popup.close();
        this.$toast.error(err?.response?.data?.message || this.$t('Failed'));
      } finally {
        this.printingId = null;
      }
    },
    buildQuery() {
      const p = { ...this.params };
      const query = {
        viewpoint: this.filter.tab || 'all',
        page: p.page,
        limit: p.limit
      };
      if (p.search) query.search = p.search;
      if (p.branch_ids && p.branch_ids.length) query.branch_ids = p.branch_ids;
      if (p.date_range && p.date_range.startDate && p.date_range.endDate) {
        query.start = moment(p.date_range.startDate).format('YYYY-MM-DD');
        query.end   = moment(p.date_range.endDate).format('YYYY-MM-DD');
      }
      const { sortBy, sortType } = this.datatable.serverOptions;
      if (sortBy === 'created_at' && sortType) {
        query.sort = sortBy + '|' + sortType;
      }
      return query;
    },
    async fetchData() {
      this.datatable.loading = true;
      try {
        const queryString = '?' + $.param(this.buildQuery());
        const res = await TaxInvoices.list(queryString);
        this.datatable.items = res.data.data || [];
        this.datatable.total = res.data.meta?.total || 0;
        if (res.data.viewpoint_cnt) {
          this.counts = { ...this.counts, ...res.data.viewpoint_cnt };
        }
        if (res.data.filter) {
          this.filters.branches = res.data.filter.branches || [];
        }
        this.hasPermission = true;
      } catch (err) {
        if (err?.response?.status === 403) {
          this.hasPermission = false;
          this.pageLoading = false;
        }
        // 401 handled by axios interceptor in router
      }
      this.datatable.loading = false;
    },
    setTab(v) {
      if (this.filter.tab === v) return;
      this.filter.tab = v;
      this.datatable.serverOptions.page = 1;
      this.params.page = 1;
      this.fetchData();
    },
    setPerPage(perPage) {
      this.params.limit = perPage;
      this.$refs.dataTable.updateRowsPerPageActiveOption(perPage);
      this.fetchData();
    },
    onApplyFilter(payload) {
      this.params.branch_ids = payload.branch_ids || [];
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    onDateRangeChange() {
      this.params.page = 1;
      this.datatable.serverOptions.page = 1;
      this.fetchData();
    },
    clearDateRange() {
      this.params.date_range = { startDate: null, endDate: null };
      this.onDateRangeChange();
    },
    formatDate(d) {
      return moment(d).format('YYYY-MM-DD');
    },
    formatDateTime(value) {
      return value ? moment(value).format('YYYY-MM-DD HH:mm') : '-';
    },
    toNumber(v) {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    },
    quickVoid(item) {
      if (!item || item.voided_at || !item.bill_id) return;
      this.$swal({
        icon: 'warning',
        iconColor: '#FF7043',
        title: this.$t('Void Tax Invoice') + (item.invoice_no ? ' ' + item.invoice_no : '') + '?',
        html: this.$t('The invoice number cannot be reused. A new number will be assigned if re-issued.'),
        input: 'text',
        inputPlaceholder: this.$t('Reason') + '...',
        inputValidator: v => !v ? this.$t('Reason') + ' ' + this.$t('is required') : null,
        showCancelButton: true,
        cancelButtonText: this.$t('Cancel'),
        confirmButtonText: this.$t('Void'),
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-secondary me-2'
        },
        reverseButtons: true
      }).then(async res => {
        if (!res.isConfirmed) return;
        try {
          await Bills.voidTaxInvoice(item.bill_id, { void_reason: res.value });
          this.$toast.success('<b>' + this.$t('Tax invoice voided') + '</b> ' + (item.invoice_no || ''));
          this.fetchData();
        } catch (err) {
          this.$toast.error(err?.response?.data?.message || this.$t('Failed'));
        }
      });
    }
  },
  watch: {
    'params.date_range': {
      deep: true,
      handler(val, old) {
        const a = (val && val.startDate) ? String(val.startDate) : '';
        const b = (val && val.endDate)   ? String(val.endDate)   : '';
        const oa = (old && old.startDate) ? String(old.startDate) : '';
        const ob = (old && old.endDate)   ? String(old.endDate)   : '';
        if (a === oa && b === ob) return;
        this.onDateRangeChange();
      }
    },
    'datatable.serverOptions'(nval) {
      this.params.page = nval.page;
      this.params.limit = nval.rowsPerPage;
      this.fetchData();
    }
  }
};
</script>

<style scoped>
.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>
